import {
  absoluteUrl,
  buildArticleJsonLd,
  buildBreadcrumbJsonLd,
  buildCreativeWorkJsonLd,
  buildPageTitle,
  buildSocialTitle,
  buildWebPageJsonLd,
  parseDisplayDate,
  resolvePageSocialMeta,
  resolveSeoImage,
} from "./seo.js";
import { buildPrerenderHeadHtml } from "./prerenderMeta.js";

/**
 * @param {string | undefined | null} title
 */
export function slugifyStoryTitle(title) {
  return (
    String(title || "untitled")
      .toLowerCase()
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "photo"
  );
}

/**
 * @param {{ title?: string } | string | null | undefined} storyOrTitle
 */
export function storySlug(storyOrTitle) {
  const title =
    typeof storyOrTitle === "string" ? storyOrTitle : storyOrTitle?.title;
  return slugifyStoryTitle(title);
}

/**
 * @param {string | { id?: string, title?: string } | null | undefined} storyOrId
 * @param {string} [title]
 */
export function storyHref(storyOrId, title) {
  if (storyOrId === null || storyOrId === undefined || storyOrId === "") {
    return "/stories";
  }

  const id = typeof storyOrId === "object" ? storyOrId.id : storyOrId;
  const storyTitle =
    typeof storyOrId === "object" ? storyOrId.title : title;

  if (id === null || id === undefined || id === "") {
    return "/stories";
  }

  const slug = slugifyStoryTitle(storyTitle);
  const encodedId = encodeURIComponent(String(id));

  if (!storyTitle || slug === "photo" || slug === "untitled") {
    return `/stories/${encodedId}`;
  }

  return `/stories/${encodedId}/${slug}`;
}

/**
 * @param {{ title?: string }} story
 * @param {string | null | undefined} [routeSlug]
 */
export function isCanonicalStoryPath(story, routeSlug) {
  const canonicalSlug = storySlug(story);
  if (!routeSlug) {
    return canonicalSlug === "photo" || canonicalSlug === "untitled";
  }
  return routeSlug === canonicalSlug;
}

/**
 * @param {import("../data/stories").STORIES[0]} story
 */
export function buildStorySeoMeta(story) {
  const path = storyHref(story);
  const pageTitle = story.seoTitle || story.title;
  const description = story.seoDescription || story.dek;
  const image = story.seoImage || story.img;
  const imageAlt =
    story.imageAlt || `${story.title} — photo story by CJ Jayamaha`;
  const publishedTime = story.publishedDate || parseDisplayDate(story.date);
  const keywords = story.tags || [];

  const social = resolvePageSocialMeta({
    image,
    imageAlt,
    title: pageTitle,
  });

  const jsonLd = [
    buildWebPageJsonLd({
      title: pageTitle,
      description,
      path,
      image,
    }),
    buildArticleJsonLd({
      title: pageTitle,
      description,
      path,
      image,
      datePublished: publishedTime,
      dateModified: publishedTime,
      section: story.cat,
      keywords,
    }),
    buildCreativeWorkJsonLd({
      title: pageTitle,
      description,
      path,
      image,
      genre: story.cat,
    }),
    buildBreadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "Stories", path: "/stories" },
      { name: story.title, path },
    ]),
  ];

  return {
    path,
    pageTitle,
    documentTitle: buildPageTitle(pageTitle),
    socialTitle: buildSocialTitle(pageTitle),
    description,
    image,
    imageAlt,
    imageUrl: social.imageUrl,
    twitterImageUrl: social.twitterImageUrl,
    imageWidth: social.imageWidth,
    imageHeight: social.imageHeight,
    imageType: social.imageType,
    twitterImageWidth: social.twitterImageWidth,
    twitterImageHeight: social.twitterImageHeight,
    ogType: "article",
    publishedTime,
    modifiedTime: publishedTime,
    keywords,
    section: story.cat,
    jsonLd,
    canonicalUrl: absoluteUrl(path),
  };
}

/**
 * Build static HTML head tags for prerendered story pages.
 *
 * @param {ReturnType<typeof buildStorySeoMeta>} meta
 */
export function buildStoryPrerenderHeadHtml(meta) {
  return buildPrerenderHeadHtml(meta);
}

/**
 * @param {import("../data/stories").STORIES[0]} story
 */
export function buildStoryShareImageUrl(story) {
  return resolveSeoImage(story.seoImage || story.img);
}
